// src/pages/CrearPersonal.js
import React, { useMemo, useState } from 'react'
import axios from 'axios'
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import QRCode from 'qrcode'

const API_SINGLE = 'https://garibcasinos.com/api/crear-personal.php'
const API_BULK = 'https://garibcasinos.com/api/personal_bulk_upsert.php'
const API_GET_ONE = 'https://garibcasinos.com/api/get_personal_by_rut.php'

// Plantilla descargable (debe existir en /public/templates/Plantilla_Carga_Masiva_Personal.xlsx)
const TEMPLATE_URL = `${process.env.PUBLIC_URL || ''}/templates/Plantilla_Carga_Masiva_Personal.xlsx`

const SEDES = [
  { value: 'Hijuelas', label: 'Hijuelas' },
  { value: 'San Felipe', label: 'San Felipe' },
  { value: 'Santiago-BrightCell', label: 'Santiago-BrightCell' },
  { value: 'UniFrutti', label: 'UniFrutti' },
]

// ---------- helpers ----------
function cleanRut(rut) {
  return (rut || '').toUpperCase().replace(/[^0-9K]/g, '').trim()
}

function formatearRut(rut) {
  rut = cleanRut(rut)
  if (rut.length < 2) return rut

  const cuerpo = rut.slice(0, -1)
  const dv = rut.slice(-1).toUpperCase()

  let out = ''
  let c = 0
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    out = cuerpo[i] + out
    c++
    if (c % 3 === 0 && i !== 0) out = '.' + out
  }
  return `${out}-${dv}`
}

function normalizeCell(v) {
  if (v == null) return ''
  const s = String(v).trim()
  if (!s) return ''
  if (s.toUpperCase() === 'NULL') return ''
  return s
}

function normalizeEmail(v) {
  const s = normalizeCell(v).toLowerCase()
  return s
}

function normalizeSede(value, areaTrabajo = '', recinto = '') {
  const raw = `${normalizeCell(value)} ${normalizeCell(areaTrabajo)} ${normalizeCell(recinto)}`.toLowerCase()

  if (
    raw.includes('san felipe') ||
    raw.includes('sanfelipe') ||
    raw.includes('sf')
  ) {
    return 'San Felipe'
  }

  if (
    raw.includes('hijuelas') ||
    raw.includes('planta hijuelas') ||
    raw.includes('hiju') ||
    raw.includes('planta2') ||
    raw.includes('planta 2')
  ) {
    return 'Hijuelas'
  }

  return ''
}

/**
 * Busca la fila donde realmente están los encabezados.
 * Soporta archivos como el tuyo, donde las primeras filas son título/fechas
 * y los encabezados comienzan recién en la fila 6.
 */
function findHeaderRow(aoa) {
  for (let i = 0; i < Math.min(aoa.length, 20); i++) {
    const row = (aoa[i] || []).map((x) => normalizeCell(x))
    const joined = row.join(' | ').toLowerCase()

    const hasRut =
      joined.includes('rut') ||
      joined.includes('número de documento') ||
      joined.includes('numero de documento')

    const hasNombre =
      joined.includes('nombre completo') ||
      joined.includes('nombre')

    if (hasRut && hasNombre) {
      return i
    }
  }
  return 0
}

// Mapea headers del Excel nuevo y antiguo a tus campos internos
function mapExcelRow(row) {
  const pick = (...keys) => {
    for (const k of keys) {
      if (row[k] != null && String(row[k]).trim() !== '') return normalizeCell(row[k])
    }
    return ''
  }

  const rutRaw = pick(
    'Rut',
    'RUT',
    'rut',
    'Empleado - Número de Documento',
    'Empleado - Numero de Documento',
    'Empleado - Número Documento'
  )

  const nombre = pick(
    'Nombre',
    'nombre',
    'Empleado - Nombre Completo',
    'Empleado - Nombre'
  )

  const centroCosto = pick(
    'CentroCosto',
    'Centro Costo',
    'Trabajo - Centro de Costo',
    'Centro de Costo'
  )

  const nombreEmpresa = pick(
    'NombreEmpresa',
    'Empresa',
    'Empresa - Nombre Empresa',
    'Nombre Empresa'
  )

  const areaTrabajo = pick(
    'AreaTrabajo',
    'Área',
    'Trabajo - Nombre Área',
    'Trabajo - Nombre Area',
    'Area Trabajo'
  )

  const rutEmpresa = pick(
    'RutEmpresa',
    'Empresa - RUT Empresa',
    'RUT Empresa',
    'Rut Empresa'
  )

  const recinto = pick(
    'Recinto',
    'ctrlit_recinto',
    'Campos Personalizados de Trabajo - ctrlit_recinto'
  )

  const email = pick(
    'Email',
    'Correo',
    'Correo Personal',
    'Empleado - Email Personal',
    'Empleado - Email'
  )

  const sedeRaw = pick(
    'Sede',
    'sede',
    'Sucursal',
    'Planta',
    'Ubicación',
    'Ubicacion'
  )

  const sedeNorm = normalizeSede(sedeRaw, areaTrabajo, recinto)

  return {
    Rut: formatearRut(rutRaw),
    Nombre: nombre,
    CentroCosto: centroCosto,
    NombreEmpresa: nombreEmpresa,
    AreaTrabajo: areaTrabajo,
    RutEmpresa: formatearRut(rutEmpresa),
    Sede: sedeNorm,
    Email: normalizeEmail(email),
    Recinto: recinto,
  }
}

export default function CrearPersonal() {
  const [formData, setFormData] = useState({
    Rut: '',
    Nombre: '',
    CentroCosto: '',
    NombreEmpresa: '',
    AreaTrabajo: '',
    RutEmpresa: '',
    Sede: 'Hijuelas',
    Email: '',
    Recinto: '',
  })

  const [mensaje, setMensaje] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  // Buscar por RUT
  const [searchingRut, setSearchingRut] = useState(false)
  const [found, setFound] = useState(false)

  // Bulk
  const [bulkRows, setBulkRows] = useState([])
  const [bulkResult, setBulkResult] = useState(null)
  const [bulkLoading, setBulkLoading] = useState(false)

  // QR masivo
  const [qrFileName, setQrFileName] = useState('')
  const [qrLoading, setQrLoading] = useState(false)
  const [qrProgress, setQrProgress] = useState(null) // { done, total }
  const [qrMensaje, setQrMensaje] = useState(null)
  const [qrError, setQrError] = useState(null)

  const fields = useMemo(
    () => [
      { key: 'Rut', label: 'RUT', placeholder: '12.345.678-9' },
      { key: 'Nombre', label: 'Nombre completo', placeholder: 'Juan Pérez' },
      { key: 'CentroCosto', label: 'Centro de costo', placeholder: 'PK02SEEM00' },
      { key: 'NombreEmpresa', label: 'Empresa', placeholder: 'Procesadora Baika SpA' },
      { key: 'AreaTrabajo', label: 'Área de trabajo', placeholder: 'Planta Hijuelas' },
      { key: 'RutEmpresa', label: 'RUT Empresa', placeholder: '76.159.154-1' },
      { key: 'Email', label: 'Email', placeholder: 'correo@empresa.com' },
      { key: 'Recinto', label: 'Recinto', placeholder: 'planta2' },
    ],
    []
  )

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }))
  }

  const clearMsgs = () => {
    setMensaje(null)
    setError(null)
  }

  const resetFormExceptRut = () => {
    setFormData((p) => ({
      ...p,
      Nombre: '',
      CentroCosto: '',
      NombreEmpresa: '',
      AreaTrabajo: '',
      RutEmpresa: '',
      Sede: 'Hijuelas',
      Email: '',
      Recinto: '',
    }))
  }

  const fetchByRut = async (rutFormatted) => {
    clearMsgs()
    setFound(false)

    const rut = formatearRut(rutFormatted)
    if (!rut || cleanRut(rut).length < 8) return

    setSearchingRut(true)
    try {
      const res = await axios.get(API_GET_ONE, { params: { rut } })

      if (res.data?.ok && res.data?.found && res.data?.row) {
        const row = res.data.row
        setFormData((p) => ({
          ...p,
          Rut: formatearRut(row.Rut || rut),
          Nombre: row.Nombre || '',
          CentroCosto: row.CentroCosto || '',
          NombreEmpresa: row.NombreEmpresa || '',
          AreaTrabajo: row.AreaTrabajo || '',
          RutEmpresa: formatearRut(row.RutEmpresa || ''),
          Sede: row.Sede === 'San Felipe' ? 'San Felipe' : 'Hijuelas',
          Email: row.Email || '',
          Recinto: row.Recinto || '',
        }))
        setFound(true)
        setMensaje('✅ RUT encontrado. Datos cargados para editar y guardar.')
      } else if (res.data?.ok && res.data?.found === false) {
        resetFormExceptRut()
        setMensaje('ℹ️ RUT no existe. Completa los datos para crearlo.')
      } else {
        setError(res.data?.error || 'No se pudo buscar el RUT.')
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al buscar el RUT.')
    } finally {
      setSearchingRut(false)
    }
  }

  const handleRutBlur = async (e) => {
    const v = formatearRut(e.target.value)
    setFormData((p) => ({ ...p, Rut: v }))
    if (v) await fetchByRut(v)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    clearMsgs()
    setSaving(true)

    try {
      const payload = {
        ...formData,
        Rut: formatearRut(formData.Rut),
        RutEmpresa: formatearRut(formData.RutEmpresa),
        Sede: formData.Sede || 'Hijuelas',
        Email: normalizeEmail(formData.Email),
        Recinto: normalizeCell(formData.Recinto),
      }

      const res = await axios.post(API_SINGLE, payload, {
        headers: { 'Content-Type': 'application/json' },
      })

      if (res.data?.ok) {
        setMensaje(found ? '✅ Personal actualizado exitosamente' : '✅ Personal creado exitosamente')
        setFound(true)
      } else {
        setError(res.data?.error || 'No se pudo guardar')
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al guardar personal')
    } finally {
      setSaving(false)
    }
  }

  // ---------- Bulk Excel ----------
  const onPickExcel = async (file) => {
    setBulkResult(null)
    clearMsgs()

    if (!file) return

    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const sheetName = wb.SheetNames[0]
      const ws = wb.Sheets[sheetName]

      // Leer como matriz para detectar la fila real de encabezados
      const aoa = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        defval: '',
        raw: false,
      })

      const headerRowIndex = findHeaderRow(aoa)

      const json = XLSX.utils.sheet_to_json(ws, {
        defval: '',
        range: headerRowIndex,
        raw: false,
      })

      const mapped = json
        .map(mapExcelRow)
        .filter((r) => r.Rut && r.Nombre)
        .map((r, idx) => ({
          ...r,
          __idx: headerRowIndex + idx + 2,
        }))

      // dedup por RUT: se queda el último del archivo
      const rutMap = new Map()
      for (const r of mapped) {
        rutMap.set(cleanRut(r.Rut), r)
      }
      const unique = Array.from(rutMap.values())

      setBulkRows(unique)
      setMensaje(
        `📄 Archivo cargado: ${unique.length} registro(s) listos para subir (deduplicados por RUT).`
      )
    } catch (e) {
      setError('No pude leer el Excel. Asegúrate que sea .xlsx/.xls y que tenga encabezados válidos.')
    }
  }

  const uploadBulk = async () => {
    setBulkResult(null)
    clearMsgs()

    if (bulkRows.length === 0) {
      setError('No hay filas cargadas para subir.')
      return
    }

    const rowsToSend = bulkRows.map(({ __idx, ...r }) => ({
      ...r,
      Rut: formatearRut(r.Rut),
      RutEmpresa: formatearRut(r.RutEmpresa),
      Email: normalizeEmail(r.Email),
      Recinto: normalizeCell(r.Recinto),
      Sede:
        r.Sede === 'San Felipe'
          ? 'San Felipe'
          : r.Sede === 'Hijuelas'
          ? 'Hijuelas'
          : '',
    }))

    setBulkLoading(true)
    try {
      const res = await axios.post(
        API_BULK,
        { rows: rowsToSend },
        { headers: { 'Content-Type': 'application/json' } }
      )

      if (res.data?.ok) {
        setBulkResult(res.data)
        setMensaje(`✅ Masivo OK: insertados ${res.data.inserted}, actualizados ${res.data.updated}.`)
      } else {
        setError(res.data?.error || 'Error en carga masiva')
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Error en carga masiva')
    } finally {
      setBulkLoading(false)
    }
  }

  // ---------- QR masivo ----------
  const onGenerarQrExcel = async (file) => {
    setQrMensaje(null)
    setQrError(null)
    setQrProgress(null)

    if (!file) return

    setQrFileName(file.name)
    setQrLoading(true)

    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const sheetName = wb.SheetNames[0]
      const ws = wb.Sheets[sheetName]

      // Leer como matriz para respetar columnas y detectar encabezados reales
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false })
      const headerRowIndex = findHeaderRow(aoa)
      const headers = (aoa[headerRowIndex] || []).map((h) => normalizeCell(h))
      const dataRows = aoa
        .slice(headerRowIndex + 1)
        .filter((row) => row.some((c) => normalizeCell(c) !== ''))

      if (headers.length === 0 || dataRows.length === 0) {
        throw new Error('El Excel no tiene filas de datos para procesar.')
      }

      // Ubicar la columna que contiene el RUT
      const rutColIdx = headers.findIndex((h) => {
        const hl = h.toLowerCase()
        return (
          hl === 'rut' ||
          hl.includes('rut') ||
          hl.includes('número de documento') ||
          hl.includes('numero de documento')
        )
      })

      if (rutColIdx === -1) {
        throw new Error('No se encontró una columna de RUT en el Excel.')
      }

      const total = dataRows.length
      setQrProgress({ done: 0, total })

      // Excel de salida (ExcelJS permite embeber imágenes en celdas)
      const outWb = new ExcelJS.Workbook()
      const outWs = outWb.addWorksheet(sheetName || 'Personal')

      outWs.columns = [
        ...headers.map((h) => ({ header: h || '', width: 20 })),
        { header: 'QR', width: 16 },
      ]
      outWs.getRow(1).font = { bold: true }

      const qrColIndex0 = headers.length // 0-based index de la columna QR

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i]
        const rutFormateado = formatearRut(row[rutColIdx])

        const excelRow = outWs.addRow([...row])
        excelRow.height = 60

        if (rutFormateado) {
          const dataUrl = await QRCode.toDataURL(rutFormateado, {
            margin: 0,
            width: 150,
          })
          const imageId = outWb.addImage({ base64: dataUrl, extension: 'png' })
          outWs.addImage(imageId, {
            tl: { col: qrColIndex0, row: excelRow.number - 1 },
            ext: { width: 60, height: 60 },
            editAs: 'oneCell',
          })
        }

        setQrProgress({ done: i + 1, total })
      }

      const buffer = await outWb.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const outName = `${file.name.replace(/\.(xlsx|xls)$/i, '')}_con_QR.xlsx`
      saveAs(blob, outName)

      setQrMensaje(`✅ QR generado para ${total} registro(s). Archivo descargado: ${outName}`)
    } catch (e) {
      setQrError(e?.message || 'No pude generar el Excel con QR. Verifica el formato del archivo.')
    } finally {
      setQrLoading(false)
    }
  }

  return (
    <>
      <section className="section">
        <div className="container">
          <div className="columns is-variable is-6">
            {/* ===== Formulario ===== */}
            <div className="column is-6">
              <div className="box" style={{ borderRadius: 16 }}>
                <h1 className="title is-4 mb-2">👤 Crear / Actualizar Personal</h1>
                <p className="has-text-grey mb-4">
                  Escribe un RUT y te cargará datos si existe. Luego puedes guardar para crear/actualizar.
                </p>

                {mensaje && <div className="notification is-success is-light">{mensaje}</div>}
                {error && <div className="notification is-danger is-light">{error}</div>}

                <form onSubmit={handleSubmit}>
                  <div className="columns is-multiline">
                    {/* RUT */}
                    <div className="column is-6">
                      <div className="field">
                        <label className="label">RUT</label>
                        <div className="control has-icons-right">
                          <input
                            className="input"
                            type="text"
                            name="Rut"
                            value={formData.Rut}
                            placeholder="12.345.678-9"
                            onChange={handleChange}
                            onBlur={handleRutBlur}
                            required
                            maxLength={12}
                          />
                          {searchingRut && (
                            <span className="icon is-small is-right">
                              <i className="fas fa-spinner fa-pulse" />
                            </span>
                          )}
                        </div>
                        <p className="help">
                          {found ? 'Encontrado: puedes editar y guardar.' : 'Si existe, se autocompletan los datos.'}
                        </p>
                      </div>
                    </div>

                    {/* Sede */}
                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Sede</label>
                        <div className="control">
                          <div className="select is-fullwidth">
                            <select name="Sede" value={formData.Sede} onChange={handleChange}>
                              {SEDES.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="help">Solo permite Hijuelas / San Felipe.</p>
                      </div>
                    </div>

                    {/* Demás campos */}
                    {fields
                      .filter((f) => f.key !== 'Rut')
                      .map((f) => (
                        <div className="column is-6" key={f.key}>
                          <div className="field">
                            <label className="label">{f.label}</label>
                            <div className="control">
                              <input
                                className="input"
                                type="text"
                                name={f.key}
                                value={formData[f.key]}
                                placeholder={f.placeholder}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  const keyLower = f.key.toLowerCase()
                                  if (keyLower.includes('rut')) {
                                    const v = formatearRut(e.target.value)
                                    setFormData((p) => ({ ...p, [f.key]: v }))
                                  }
                                  if (f.key === 'Email') {
                                    setFormData((p) => ({ ...p, Email: normalizeEmail(e.target.value) }))
                                  }
                                }}
                                required={f.key === 'Nombre'}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="field mt-2">
                    <div className="control">
                      <button
                        className={`button is-primary is-fullwidth ${saving ? 'is-loading' : ''}`}
                        type="submit"
                        disabled={saving}
                      >
                        {found ? 'Guardar cambios' : 'Crear / Guardar'}
                      </button>
                    </div>
                  </div>

                  <div className="buttons is-right mt-2">
                    <button
                      type="button"
                      className="button is-light"
                      onClick={() => {
                        clearMsgs()
                        setFound(false)
                        setFormData({
                          Rut: '',
                          Nombre: '',
                          CentroCosto: '',
                          NombreEmpresa: '',
                          AreaTrabajo: '',
                          RutEmpresa: '',
                          Sede: 'Hijuelas',
                          Email: '',
                          Recinto: '',
                        })
                      }}
                      disabled={saving || searchingRut}
                    >
                      Limpiar
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* ===== Carga masiva ===== */}
            <div className="column is-6">
              <div className="box" style={{ borderRadius: 16 }}>
                <h2 className="title is-4 mb-2">📥 Carga masiva desde Excel</h2>
                <p className="has-text-grey mb-3">
                  Importa un <b>.xlsx</b> y se crearán/actualizarán por RUT.
                </p>

                <div className="buttons mb-3">
                  <a className="button is-info is-light" href={TEMPLATE_URL} download>
                    ⬇️ Descargar plantilla Excel
                  </a>
                </div>

                <div className="field">
                  <label className="label">Archivo Excel</label>
                  <div className="control">
                    <input
                      className="input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => onPickExcel(e.target.files?.[0] || null)}
                    />
                  </div>
                  <p className="help">
                    Soporta el formato nuevo del reporte y también el formato anterior.<br />
                    Columnas detectadas: <b>Empleado - Número de Documento</b>, <b>Empleado - Nombre Completo</b>, <b>Trabajo - Centro de Costo</b>, <b>Empresa - Nombre Empresa</b>, <b>Trabajo - Nombre Área</b>, <b>Empresa - RUT Empresa</b>, <b>Campos Personalizados de Trabajo - ctrlit_recinto</b>, <b>Empleado - Email Personal</b>.
                  </p>
                </div>

                <div className="buttons mt-3">
                  <button
                    className={`button is-link ${bulkLoading ? 'is-loading' : ''}`}
                    type="button"
                    onClick={uploadBulk}
                    disabled={bulkLoading || bulkRows.length === 0}
                  >
                    Subir masivo (crear/actualizar)
                  </button>

                  <button
                    className="button is-light"
                    type="button"
                    onClick={() => {
                      setBulkRows([])
                      setBulkResult(null)
                      clearMsgs()
                    }}
                    disabled={bulkLoading}
                  >
                    Limpiar
                  </button>
                </div>

                {/* Preview */}
                <div className="mt-4">
                  <div className="is-flex is-justify-content-space-between is-align-items-center">
                    <b>Preview</b>
                    <span className="tag is-info is-light">{bulkRows.length} filas</span>
                  </div>

                  <div className="table-container mt-2" style={{ maxHeight: 360, overflow: 'auto' }}>
                    <table className="table is-fullwidth is-striped is-hoverable is-size-7">
                      <thead>
                        <tr>
                          <th>RUT</th>
                          <th>Nombre</th>
                          <th>Centro Costo</th>
                          <th>Área</th>
                          <th>Empresa</th>
                          <th>Recinto</th>
                          <th>Email</th>
                          <th>Sede</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkRows.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="has-text-centered has-text-grey">
                              Carga un Excel para ver la vista previa.
                            </td>
                          </tr>
                        ) : (
                          bulkRows.slice(0, 200).map((r) => (
                            <tr key={cleanRut(r.Rut)}>
                              <td>{r.Rut}</td>
                              <td>{r.Nombre}</td>
                              <td>{r.CentroCosto}</td>
                              <td>{r.AreaTrabajo}</td>
                              <td>{r.NombreEmpresa}</td>
                              <td>{r.Recinto}</td>
                              <td>{r.Email}</td>
                              <td>{r.Sede}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {bulkRows.length > 200 && <p className="help">Mostrando 200 filas para rendimiento.</p>}

                  {bulkResult?.ok && (
                    <div className="notification is-success is-light mt-3">
                      <b>Resultado:</b> Insertados {bulkResult.inserted} · Actualizados {bulkResult.updated} · Errores {bulkResult.errors?.length || 0}
                      {Array.isArray(bulkResult.errors) && bulkResult.errors.length > 0 && (
                        <div className="mt-2">
                          <b>Errores (primeros 10):</b>
                          <ul>
                            {bulkResult.errors.slice(0, 10).map((e, idx) => (
                              <li key={idx}>
                                Fila {e.row}: {e.error} {e.rut ? `(RUT: ${e.rut})` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ===== Creación QR masivo ===== */}
              <div className="box mt-5" style={{ borderRadius: 16 }}>
                <h2 className="title is-4 mb-2">🔳 Creación QR masivo</h2>
                <p className="has-text-grey mb-3">
                  Sube un <b>.xlsx</b>/<b>.xls</b> con una columna de RUT. Se generará un nuevo Excel
                  con una columna extra <b>QR</b> que contiene el código QR del RUT formateado de cada
                  persona.
                </p>

                {qrMensaje && <div className="notification is-success is-light">{qrMensaje}</div>}
                {qrError && <div className="notification is-danger is-light">{qrError}</div>}

                <div className="field">
                  <label className="label">Archivo Excel</label>
                  <div className="control">
                    <input
                      className="input"
                      type="file"
                      accept=".xlsx,.xls"
                      disabled={qrLoading}
                      onChange={(e) => onGenerarQrExcel(e.target.files?.[0] || null)}
                    />
                  </div>
                  <p className="help">
                    Detecta automáticamente la fila de encabezados y la columna de RUT (acepta
                    encabezados como <b>Rut</b>, <b>RUT</b> o <b>Número de Documento</b>).
                  </p>
                </div>

                {qrFileName && (
                  <p className="has-text-grey-dark mb-2">
                    Archivo: <b>{qrFileName}</b>
                  </p>
                )}

                {qrLoading && (
                  <div className="mb-2">
                    <progress
                      className="progress is-link"
                      value={qrProgress?.done || 0}
                      max={qrProgress?.total || 1}
                    >
                      {qrProgress ? Math.round((qrProgress.done / qrProgress.total) * 100) : 0}%
                    </progress>
                    <p className="help">
                      Generando QR {qrProgress?.done || 0} / {qrProgress?.total || 0}…
                    </p>
                  </div>
                )}

                <p className="help">
                  El archivo resultante se descarga automáticamente con el sufijo{' '}
                  <b>_con_QR.xlsx</b>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}